import React, { useEffect, useState } from 'react';
import { Button, Tooltip, ClickAwayListener } from '@mui/material';
import { WrapComment, CommentTitle } from './Styled'
import { Close } from '@mui/icons-material';
import { useTheme } from 'styles';
import { CacheProvider } from '@emotion/react';
import { cacheLtr } from 'styles/theme';

/**
 * Comment (onboarding tooltip wrapper)
 * Wraps a child element and shows a dismissible hint tooltip — used for guided UX
 * (e.g. record button hints, page-flip instructions).
 *
 * **How it works**
 * - Without `title`: renders `children` only (no tooltip).
 * - With `title`: shows a styled tooltip anchored to `children`.
 * - Visibility requires both `open` (from parent) and internal dismiss state.
 * - User dismisses via the close (X) icon; tooltip stays hidden until remount.
 * - Hover/focus/touch listeners are disabled — display is fully controlled by props.
 *
 * Exported as `Comment` from `components/Form`.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Element the tooltip is anchored to.
 * @param {string} [props.title] - Hint text. When omitted, only `children` render.
 * @param {boolean} [props.open] - Parent-controlled visibility flag.
 * @param {string} [props.placement='left'] - MUI Tooltip placement (`top`, `right`, `bottom`, `left`, etc.).
 * @param {number} [props.right] - Horizontal offset for tooltip positioning.
 * @param {number} [props.bottom] - Vertical offset for tooltip positioning.
 * @example
 * <Comment title="Click to start recording" open={showHint} bottom={22}>
 *   <RecordButton />
 * </Comment>
 * @example
 * // Spread config object
 * const comment = { title: hintText, open: shouldShow, bottom: 22 };
 * <Comment {...comment}>{children}</Comment>
 */
const Comment = (props) => {
    const { children, title, open, right, bottom, placement = "left" } = props
    const [openVal, setOpenVal] = useState(true)

    const { Layout, Fonts } = useTheme()

    const handleTooltipClose = () => {
        setOpenVal(false);
    };

    const handleTooltipOpen = () => {
        setOpenVal(true);
    };

    return (
        title ?
            <ClickAwayListener onClickAway={() => { }}>
                <div>
                    <CacheProvider value={cacheLtr}>
                        <WrapComment
                            bottom={bottom}
                            right={right}
                            arrow
                            PopperProps={{
                                disablePortal: true,
                            }}
                            TransitionProps={{ timeout: 1500 }}
                            // onClose={handleTooltipClose}
                            open={open && openVal}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            placement={placement}
                            title={
                                <CommentTitle>
                                    <div className='comment-icon'>
                                        <Close fontSize='small' color="thirdly2" onClick={e => handleTooltipClose(e)}></Close>
                                    </div>
                                    <div className='comment-title' style={{ ...Layout.grow, height: 70, ...Fonts.textDark }}>
                                        {title}
                                    </div>
                                </CommentTitle>
                            }
                        >
                            {children}
                        </WrapComment>
                    </CacheProvider>
                </div>
            </ClickAwayListener>
            :
            children
    );
}

export default Comment
